/**
 * PATH: packages/tenant-shared/src/integrations/xeroStatusState.ts
 * FILE_ID: TENANTSHARED-INTEGRATIONS-XEROSTATUS-STATE-001
 * PURPOSE: Own the shared org-level Xero status state and polling hook.
 * WHY: Keep status normalisation separate from OAuth and tenant actions.
 * FLOW: API status/settings -> normalise -> shared hook cache.
 * DEPENDENCIES: React, tenant API client.
 * RELATED: useXeroStatus.ts, xeroStatusOAuth.ts, xeroStatusConnections.ts.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import api, { API_BASE, getAccessToken } from '../hooks/useApi'

export const XERO_STATUS_REFRESH_EVENT = 'quotech.xero.status.refresh'

/**
 * Why a reconnect is needed. Surfaced by sam-api in the
 * ``reconnect_reason`` field (qb!2609). Lets the UI render an accurate
 * CTA copy instead of the legacy "missing payroll permissions" message
 * ? which lied to operators whose Xero genuinely had payroll scopes
 * but whose refresh token had been invalidated server-side.
 *
 * - ``token_expired``: the refresh token at Xero is dead (revoked,
 *   60-day idle expiry, or a parallel refresh swallowed it). Operator
 *   must re-authorise from scratch ? no scope decision involved.
 * - ``missing_payroll_scopes``: operator authorised Xero but de-selected
 *   payroll on the consent screen. Re-authorise and tick the payroll boxes.
 * - ``null``: nothing to reconnect (or sam-api too old to report).
 */
export type XeroReconnectReason = 'token_expired' | 'missing_payroll_scopes' | null

export interface XeroStatus {
  /** Loading on first fetch; true means we have a definitive answer. */
  loaded: boolean
  /** True if the API was reachable. False = network/auth/server error. */
  reachable: boolean
  connected: boolean
  tenantName: string | null
  /**
   * True when org-level connection requires re-authorisation. The
   * underlying *reason* is in ``reconnectReason`` ? historically this
   * boolean meant only "missing payroll scopes" but as of qb!2609 it
   * also covers the (much more common) dead-refresh-token case.
   */
  needsReconnection: boolean
  /** See ``XeroReconnectReason``. ``null`` when ``needsReconnection=false``. */
  reconnectReason: XeroReconnectReason
  lastSyncAt: string | null
  /** Set to a human-readable error when reachable=false. */
  message: string | null
  /**
   * Whether Xero is RELEVANT to this organisation, from the per-org
   * `OrganisationSettings.enable_xero` flag (`GET /settings/organisation`).
   * When ``false`` the operator has marked Xero "not relevant" (e.g. they
   * run on another accounting system) and every Xero prompt ? the shell
   * banner, the Bills-hub banner, the settings connect card ? must be
   * suppressed. FAIL-OPEN: defaults to ``true`` and only an explicit
   * ``false`` disables, so a missing field (older backend) or a fetch
   * error never hides a genuinely-needed "connect Xero" prompt.
   */
  enabled: boolean
}

interface XeroStatusPayload {
  connected: boolean
  tenant_name?: string | null
  account_name?: string | null
  account_email?: string | null
  is_valid?: boolean
  enable_xero?: boolean
  has_payroll_scopes?: boolean
  needs_reconnection?: boolean
  /** New in qb!2609. Optional ? older sam-api builds omit it. */
  reconnect_reason?: XeroReconnectReason
  /** New in qb!2609 ? `'connected' | 'expired' | 'error' | 'available'`. */
  connection_status?: string | null
  last_sync_at?: string | null
  last_sync_counts?: Record<string, number> | null
  error?: string
}

const INITIAL: XeroStatus = {
  loaded: false,
  reachable: true,
  connected: false,
  tenantName: null,
  needsReconnection: false,
  reconnectReason: null,
  lastSyncAt: null,
  message: null,
  // Fail-open: assume Xero is relevant until the org flag says otherwise.
  enabled: true,
}

/** Master Django mounts `/api/xero/status/`; SAM/Steel retain `/xero/auth/status`. */
export function usesDjangoXeroContract(): boolean {
  return (
    API_BASE === '/api' ||
    /quotech-backend\.quotech\.ai\/api$/i.test(API_BASE)
  )
}

function normaliseDjangoStatus(data: XeroStatusPayload, enabled: boolean): XeroStatus {
  const connected = data.connected === true
  const needsReconnection =
    connected && (data.needs_reconnection === true || data.is_valid === false)
  return {
    loaded: true,
    reachable: true,
    connected: connected && !needsReconnection,
    tenantName: data.account_name ?? data.tenant_name ?? null,
    needsReconnection,
    reconnectReason: needsReconnection ? 'token_expired' : null,
    lastSyncAt: data.last_sync_at ?? null,
    message: null,
    enabled: data.enable_xero === false ? false : enabled,
  }
}

/**
 * Module-scope cache for the per-org `enable_xero` flag. Unlike the
 * connection status (which polls every 60s) this flag is near-static for
 * a session, so we fetch it once and share it across every useXeroStatus
 * consumer. `null` = not yet fetched. FAIL-OPEN: any error or missing
 * field resolves to `true`.
 */
let g_xeroEnabledCache: boolean | null = null
let g_xeroEnabledInflight: Promise<boolean> | null = null

interface OrgSettingsXeroPayload {
  enable_xero?: boolean
}

/**
 * Resolve the org's `enable_xero` flag (cached). Only an explicit `false`
 * disables Xero; everything else (missing field on an older backend, a
 * network/permission error, any non-false value) resolves to `true`.
 */
export async function fetchXeroEnabled(): Promise<boolean> {
  if (g_xeroEnabledCache !== null) return g_xeroEnabledCache
  // Same cold-mount gate as useXeroStatus.refresh — without a bearer
  // the /xero/status probe 401s and we would incorrectly cache
  // fail-open `true` forever for the session.
  if (!getAccessToken()) return true
  if (!g_xeroEnabledInflight) {
    const master = usesDjangoXeroContract()
    g_xeroEnabledInflight = api
      .get<OrgSettingsXeroPayload & XeroStatusPayload>(
        master ? '/xero/status/' : '/settings/organisation',
      )
      .then((resp) => {
        const value = resp?.data?.enable_xero
        g_xeroEnabledCache = value === false ? false : true
        return g_xeroEnabledCache
      })
      .catch(() => {
        g_xeroEnabledCache = true
        return true
      })
      .finally(() => {
        g_xeroEnabledInflight = null
      })
  }
  return g_xeroEnabledInflight
}

/**
 * Drop the cached `enable_xero` value so the next read re-fetches it.
 * Call after the operator toggles "We use Xero" in Settings so the
 * banners reflect the new state without a hard reload (the settings save
 * also dispatches `XERO_STATUS_REFRESH_EVENT`, which re-reads the flag).
 */
export function bustXeroEnabledCache(): void {
  g_xeroEnabledCache = null
  g_xeroEnabledInflight = null
}

/**
 * Single source of truth for the org's Xero connection state. Polls while
 * visible and refreshes in response to the shared OAuth/settings event.
 */
export function useXeroStatus() {
  const [status, setStatus] = useState<XeroStatus>(INITIAL)
  const inFlight = useRef(false)

  const refresh = useCallback(async () => {
    // Cold-mount race (same class as integration-events): route data
    // fires before ApiAuthGate.setAccessToken. Hitting /xero/status
    // without a bearer prints a 2ms 401 in DevTools and is not a real
    // auth failure — skip until the bearer is bound; the visibility /
    // 60s poll below retries.
    if (!getAccessToken()) return
    if (inFlight.current) return
    inFlight.current = true
    try {
      const master = usesDjangoXeroContract()
      const { data } = await api.get<XeroStatusPayload>(
        master ? '/xero/status/' : '/xero/auth/status',
      )
      if (master) {
        setStatus((previous) => normaliseDjangoStatus(data, previous.enabled))
        return
      }
      // sam-api returns HTTP 200 with `{ connected: false, error }` when
      // its OWN internal call to Quotech fails (sam-api xero.py
      // `xero_auth_status` exception path). That is NOT a definitive
      // "Xero is not connected" ? we simply couldn't determine the
      // state. Treating it as `connected=false, reachable=true` made the
      // shell banner flash a false "Xero is not connected" on a transient
      // upstream hiccup (2026-06-02 operator complaint: *"Its already
      // connected to ... xero why did i get here .. why are your banners
      // always bugging"*). Map the error payload onto the same
      // `reachable=false` outcome as a hard network failure so the banner
      // (which only renders when `reachable===true`) stays quiet until we
      // get a clean answer.
      if (data.error) {
        setStatus((prev) => ({
          loaded: true,
          reachable: false,
          connected: false,
          tenantName: null,
          needsReconnection: false,
          reconnectReason: null,
          lastSyncAt: null,
          message: data.error ?? 'Could not check Xero status',
          enabled: prev.enabled,
        }))
        return
      }
      const needsReconnection = !!data.needs_reconnection
      // Back-compat: older sam-api builds (pre qb!2609) don't return
      // ``reconnect_reason``. Default to the legacy meaning of
      // ``needs_reconnection`` (== "missing payroll") when the field is
      // absent so we don't regress copy on a half-deployed environment.
      let reconnectReason: XeroReconnectReason = null
      if (needsReconnection) {
        const raw = data.reconnect_reason
        reconnectReason =
          raw === 'token_expired' || raw === 'missing_payroll_scopes'
            ? raw
            : 'missing_payroll_scopes'
      }
      // Preserve the org `enabled` flag across connection refreshes (it's
      // resolved by a separate, cached fetch below).
      setStatus((prev) => ({
        loaded: true,
        reachable: true,
        connected: !!data.connected,
        tenantName: data.tenant_name ?? null,
        needsReconnection,
        reconnectReason,
        lastSyncAt: data.last_sync_at ?? null,
        message: null,
        enabled: prev.enabled,
      }))
    } catch (err) {
      setStatus((prev) => ({
        loaded: true,
        reachable: false,
        connected: false,
        tenantName: null,
        needsReconnection: false,
        reconnectReason: null,
        lastSyncAt: null,
        message: err instanceof Error ? err.message : 'Could not check Xero status',
        enabled: prev.enabled,
      }))
    } finally {
      inFlight.current = false
    }
  }, [])

  const loadEnabled = useCallback(() => {
    void fetchXeroEnabled().then((enabled) => {
      setStatus((prev) => (prev.enabled === enabled ? prev : { ...prev, enabled }))
    })
  }, [])

  // Initial fetch + poll while tab is visible.
  useEffect(() => {
    void refresh()
    loadEnabled()
    // Retry shortly after mount so a late ApiAuthGate.setAccessToken
    // still populates Xero state without waiting for the 60s poll.
    const bootRetry = window.setTimeout(() => {
      void refresh()
      loadEnabled()
    }, 750)
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh()
    }, 60_000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    const onForceRefresh = () => {
      // A connect/disconnect OR a "We use Xero" settings toggle fired this.
      // Bust the org-flag cache so the relevance flag re-reads too.
      bustXeroEnabledCache()
      void refresh()
      loadEnabled()
    }
    window.addEventListener(XERO_STATUS_REFRESH_EVENT, onForceRefresh)
    return () => {
      window.clearTimeout(bootRetry)
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener(XERO_STATUS_REFRESH_EVENT, onForceRefresh)
    }
  }, [refresh, loadEnabled])

  return { status, refresh }
}
